import { translateApiError, useTranslation } from '@jq4race/i18n';
import type {
  ApplyCompResponse,
  RegisterCashPaymentResponse,
  TicketSearchResultItem,
} from '@jq4race/shared';
import { Button } from '@jq4race/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ScanLine } from 'lucide-react';
import { useState } from 'react';
import { showStatusToast } from '../../../../app/status-toast-store';
import { QrScanner } from '../../../../shared/ui/QrScanner';
import { useCompsConfig } from '../../../location-context/hooks/use-comps-config';
import { useApplyComp } from '../../../pickup-queue/hooks/use-apply-comp';
import { useRegisterCashPayment } from '../../../pickup-queue/hooks/use-register-cash-payment';
import { CashPaymentDialog } from '../../../pickup-queue/ui/components/CashPaymentDialog';
import { CompDialog } from '../../../pickup-queue/ui/components/CompDialog';
import { PageHeader } from '../../../shell/ui/PageHeader';
import { SEARCH_TICKETS_KEY, useSearchTickets } from '../../hooks/use-search-tickets';
import { SearchInput } from '../components/SearchInput';
import { SearchResultList } from '../components/SearchResultList';

export function SearchPage() {
  const { t } = useTranslation('search');
  const [query, setQuery] = useState('');
  const { data = [], isFetching } = useSearchTickets(query);

  // valet-keychain-qr — scan-to-deliver: a scanned code is fed as the same
  // `q=` search param as manual typing (searchActiveByLocation already
  // matches qrTag — Phase 3). The backend scopes search to the valet's own
  // location, so a code from a DIFFERENT location naturally resolves to zero
  // rows here too — no separate "foreign location" branch needed, and no L2
  // data ever reaches this client.
  //
  // sdd-verify follow-up (WARNING fix): the server-side search is a generic
  // substring OR-match (plate/phone/name/ticketNumber/qrTag) — reusing its
  // raw response for the CAMERA-SCAN path would let a scanned code that is a
  // strict PREFIX of another active ticket's qrTag (e.g. scanning "LAX-0042"
  // while a "LAX-00421" ticket is also active) incorrectly resolve to that
  // longer, unrelated ticket. The scan path additionally filters the search
  // response down to an EXACT (trimmed, case-sensitive — QrPrefix is
  // case-sensitive) qrTag match before treating it as found. Manual typed
  // search is intentionally unaffected — this tightening applies ONLY to the
  // decoded-from-camera path per spec (pwa-qr-scanning: scan resolves tag ->
  // ticket).
  const [scanning, setScanning] = useState(false);
  const [scannedQuery, setScannedQuery] = useState<string | null>(null);
  const isScannedQueryCurrent = scannedQuery !== null && scannedQuery === query;
  const scannedExactMatch = isScannedQueryCurrent
    ? (data.find((item) => item.qrTag?.trim() === scannedQuery) ?? null)
    : null;
  const scanNotFound = isScannedQueryCurrent && !isFetching && !scannedExactMatch;
  // Only the exact match (never the raw substring-matched list) is shown
  // when the current query originated from a scan.
  const displayItems = isScannedQueryCurrent
    ? scannedExactMatch
      ? [scannedExactMatch]
      : []
    : data;

  function handleManualQueryChange(next: string) {
    setScannedQuery(null);
    setQuery(next);
  }

  function handleScanDecode(text: string) {
    setScanning(false);
    setScannedQuery(text);
    setQuery(text);
  }

  const qc = useQueryClient();
  const cashMutation = useRegisterCashPayment();
  const compMutation = useApplyComp();

  // JQ4-19 slice 3: comp config for conditional button visibility (Scenario 5)
  const { comps } = useCompsConfig();

  // JQ4-54 (JQ4-38 follow-up) — cash payment flow state machine (mirrors HomePage)
  const [cashTicketId, setCashTicketId] = useState<string | null>(null);
  const [registeringCashId, setRegisteringCashId] = useState<string | null>(null);
  const [cashErrorMessage, setCashErrorMessage] = useState<string | null>(null);

  // JQ4-19: comp flow state machine
  const [compTicketId, setCompTicketId] = useState<string | null>(null);
  const [compErrorMessage, setCompErrorMessage] = useState<string | null>(null);
  const [applyingCompId, setApplyingCompId] = useState<string | null>(null);

  // Derive the selected items for the dialog descriptions
  const cashTicketItem = data.find((item) => item.id === cashTicketId);
  const compTicketItem = data.find((item) => item.id === compTicketId);

  function handleRegisterCash(id: string) {
    setCashTicketId(id);
    setCashErrorMessage(null);
  }

  function handleCashConfirm(amountCents: number) {
    if (!cashTicketId) return;
    const id = cashTicketId;
    setRegisteringCashId(id);
    setCashErrorMessage(null);

    cashMutation.mutate(
      { ticketId: id, amountCents },
      {
        onSuccess: (response: RegisterCashPaymentResponse) => {
          // Optimistic update: patch the ticket in the current search result without a refetch.
          // `query` is captured safely: CashPaymentDialog is a modal radix Dialog (focus-trapped
          // overlay), so the operator cannot change the search input between opening the dialog and
          // confirming — the captured key always matches the visible result list.
          qc.setQueryData<TicketSearchResultItem[]>(SEARCH_TICKETS_KEY(query), (prev) =>
            prev?.map((item) =>
              item.id === id ? { ...item, paymentStatus: response.paymentStatus } : item,
            ),
          );
          setRegisteringCashId(null);
          setCashTicketId(null);
          // QA R2-15: confirm what was recorded (the dialog used to close silently).
          showStatusToast(
            t('pickup-queue:toast.cashRecorded', { ticketNumber: response.ticketNumber }),
          );
        },
        onError: (err) => {
          setRegisteringCashId(null);
          const message = translateApiError(err, t, { fallback: t('common:errors.unexpected') });
          setCashErrorMessage(message);
          // Dialog stays open — cashTicketId is still set
        },
      },
    );
  }

  function handleCashCancel() {
    setCashTicketId(null);
    setCashErrorMessage(null);
    setRegisteringCashId(null);
  }

  // JQ4-19: comp handlers (mirrors cash payment pattern)
  function handleApplyComp(id: string) {
    setCompTicketId(id);
    setCompErrorMessage(null);
  }

  function handleCompConfirm(reason: string) {
    if (!compTicketId) return;
    const id = compTicketId;
    setApplyingCompId(id);
    setCompErrorMessage(null);

    compMutation.mutate(
      { ticketId: id, reason },
      {
        onSuccess: (response: ApplyCompResponse) => {
          // Optimistic update: patch the ticket in the current search result without a refetch.
          qc.setQueryData<TicketSearchResultItem[]>(SEARCH_TICKETS_KEY(query), (prev) =>
            prev?.map((item) =>
              item.id === id ? { ...item, paymentStatus: response.paymentStatus } : item,
            ),
          );
          setApplyingCompId(null);
          setCompTicketId(null);
          showStatusToast(
            t('pickup-queue:toast.compRecorded', { ticketNumber: compTicketItem?.ticketNumber }),
          );
        },
        onError: (err) => {
          setApplyingCompId(null);
          const message = translateApiError(err, t, { fallback: t('common:errors.unexpected') });
          setCompErrorMessage(message);
          // Dialog stays open — compTicketId is still set
        },
      },
    );
  }

  function handleCompCancel() {
    setCompTicketId(null);
    setCompErrorMessage(null);
    setApplyingCompId(null);
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-24">
      <PageHeader title={t('title')} />

      {scanning ? (
        <QrScanner
          label={t('scanLabel')}
          onDecode={handleScanDecode}
          onCancel={() => setScanning(false)}
        />
      ) : (
        <div className="flex gap-2">
          <div className="flex-1">
            <SearchInput value={query} onChange={handleManualQueryChange} />
          </div>
          <Button
            type="button"
            variant="ghost"
            className="h-12 px-3"
            onClick={() => setScanning(true)}
            aria-label={t('scanAria')}
          >
            <ScanLine className="size-4" />
          </Button>
        </div>
      )}

      {/* valet-keychain-qr — explicit not-found state for a scanned code
          that matched no active ticket at this location (distinct from the
          generic "keep typing" empty state below). No ticket opens. */}
      {scanNotFound && (
        <p role="alert" className="py-2 text-center font-sans text-sm text-destructive">
          {t('scanNotFound')}
        </p>
      )}

      {isFetching ? (
        <p className="py-8 text-center font-sans text-sm text-muted-foreground">{t('searching')}</p>
      ) : scanNotFound ? null : (
        <SearchResultList
          items={displayItems}
          query={query}
          onRegisterCash={handleRegisterCash}
          onApplyComp={comps.authorizedForActor ? handleApplyComp : undefined}
          compsAuthorized={comps.authorizedForActor}
        />
      )}

      {/* JQ4-54 (JQ4-38 follow-up): cash payment dialog */}
      {cashTicketItem ? (
        <CashPaymentDialog
          open={cashTicketId !== null}
          ticketId={cashTicketItem.id}
          ticketNumber={cashTicketItem.ticketNumber}
          plate={cashTicketItem.plate}
          onConfirm={handleCashConfirm}
          onCancel={handleCashCancel}
          errorMessage={cashErrorMessage}
          submitting={registeringCashId !== null}
        />
      ) : null}

      {/* JQ4-19 slice 3: comp (cortesía) dialog */}
      {compTicketItem ? (
        <CompDialog
          open={compTicketId !== null}
          ticketNumber={compTicketItem.ticketNumber}
          plate={compTicketItem.plate}
          onConfirm={handleCompConfirm}
          onCancel={handleCompCancel}
          errorMessage={compErrorMessage}
          submitting={applyingCompId !== null}
        />
      ) : null}
    </div>
  );
}
